import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, FormSection, resetSection, WrappedFieldProps } from 'redux-form';
import cn from 'classnames';
import { rm } from 'utils';
import { PageState } from 'models/PageState';
import { isCheckoutPage } from 'models/CheckoutPageModel';
import {
  checkoutForm,
  checkoutFormName,
  isVouchersCoveringExpenses
} from 'views/CheckoutPage/selector';
import { ICart } from 'models/ICart';
import FormError from 'components/Shared/Form/FormError';
import { Confirm } from './Confirm';
import { PaymentMethod } from './paymentMethod';
import { PaymentMethod as PaymentMethodModel } from 'models/PaymentMethod';
import { Discount } from './Discount';
import { AlertNotifications } from 'components/AlertNotification';
import { getPaymentAlerts } from 'components/AlertNotification/selector';
import styles from './styles.scss';
import { SectionTitle } from 'views/CheckoutPage/SectionTitle';
import { GtmAddPaymentInfo, GtmChangePaymentMethod } from 'store/googleTagManager/actions';
import { Show } from '@bygghemmait/components';
import { getShouldHideDiscountfield } from 'components/Cart/selector';
import { ActiveDiscounts } from './ActiveDiscounts';
import { withCache } from 'utils/withCache';
import { ThunkDispatch } from 'components/Shared/connect';

interface Props {
  disabled: boolean;
  handleSubmit: () => Promise<void>;
}

const getPaymentMethods = (state: PageState) =>
  isCheckoutPage(state.currentPage) ? state.currentPage.paymentMethods : [];
const getActivePaymentMethod = (state: PageState): string =>
  checkoutForm(state, 'payment.method') as string;
const getCart = (state: PageState): ICart => state.cart;

interface EmitAddPaymentInfo {
  dispatch: ThunkDispatch;
}
const emitAddPaymentInfo = ({ dispatch }: EmitAddPaymentInfo) => {
  const addPaymentInfoKey = 'added_payment_info';
  const shouldEmitAddPaymentInfo = withCache(cache => {
    const hasEmitted = cache.get(addPaymentInfoKey);
    if (hasEmitted) return false;
    cache.set(addPaymentInfoKey, true);
    return true;
  });

  if (!shouldEmitAddPaymentInfo) return;
  dispatch(GtmAddPaymentInfo());
};

export const Payment = ({ disabled, handleSubmit }: Props) => {
  const dispatch = useDispatch();
  const reset = useCallback(
    () => dispatch(resetSection(checkoutFormName, 'payment.partPaymentItem')),
    [dispatch]
  );
  const paymentMethods = useSelector(getPaymentMethods);
  const activePaymentMethod = useSelector(getActivePaymentMethod);
  const cart = useSelector(getCart);
  const paymentAlerts = useSelector(getPaymentAlerts);
  const vouchersCoveringExpenses = isVouchersCoveringExpenses(cart);
  const shouldHideDiscountField = useSelector(getShouldHideDiscountfield);

  if (!disabled) {
    emitAddPaymentInfo({ dispatch });
  }

  const handleDispatchGtmEvent = (newValue: string) => {
    dispatch(GtmChangePaymentMethod(newValue));
  };

  const content = (
    <>
      <AlertNotifications alerts={paymentAlerts} />
      <FormSection name="payment">
        {!vouchersCoveringExpenses && (
          <Field
            component={renderPaymentMethods}
            name="method"
            required
            disabled={disabled}
            paymentMethods={paymentMethods}
            reset={reset}
            activePaymentMethod={activePaymentMethod}
            dispatchGtmChangePaymentMethod={handleDispatchGtmEvent}
          />
        )}
        <Show when={!shouldHideDiscountField}>
          <Discount disabled={disabled} />
        </Show>
        <ActiveDiscounts />
      </FormSection>
      <Confirm handleSubmit={handleSubmit} disabled={disabled} />
    </>
  );

  return (
    <div
      className={cn(styles.paymentContainer, {
        [styles.disabled]: disabled
      })}
    >
      <SectionTitle title={rm('checkout_payment_title')} className={styles.sectionTitle} />
      {content}
    </div>
  );
};

interface RenderPaymentMethodProps extends WrappedFieldProps {
  disabled: boolean;
  paymentMethods: PaymentMethodModel[];
  reset: () => void;
  activePaymentMethod: string;
  dispatchGtmChangePaymentMethod: (newPaymentMethod: string) => void;
}

const instoreCommonName = 'svea_payment_instore';

const renderPaymentMethods = ({
  disabled,
  paymentMethods,
  reset,
  activePaymentMethod,
  input,
  meta,
  dispatchGtmChangePaymentMethod
}: RenderPaymentMethodProps) => {
  return (
    <ul className={styles.paymentMethods}>
      {paymentMethods.map(method => {
        const isActive = method.commonName === activePaymentMethod;
        const isInstore = method.commonName === instoreCommonName;

        return (
          <li
            key={method.commonName}
            className={cn(styles.paymentMethod, {
              [styles.active]: isActive,
              [styles.admin]: isInstore
            })}
          >
            <PaymentMethod
              method={method}
              input={input}
              reset={reset}
              isActive={isActive}
              disabled={disabled}
              dispatchGtmChangePaymentMethod={dispatchGtmChangePaymentMethod}
            />
          </li>
        );
      })}
      {meta.error && meta.touched && <FormError error={meta.error} />}
    </ul>
  );
};
